import { DownloadState } from '../db/types'
import { DownloadEntry } from './expand'

/** All the queue needs of an entry. Its type and id are the expander's business, not the queue's. */
export type QueueEntry = Pick<DownloadEntry, 'urls' | 'cacheKey'>

/** The slice of the Cache API this needs, so the tests can drive it against a fake. */
export interface DownloadCache {
	match: (key: string) => Promise<Response | undefined>
	put: (key: string, response: Response) => Promise<void>
	delete: (key: string) => Promise<boolean>
}

export interface QueueDeps<E extends QueueEntry = QueueEntry> {
	cache: DownloadCache
	fetch: (url: string) => Promise<Response>
	/** Resolves once this deploy's worker is active, rejects if its install failed. */
	workerReady: () => Promise<void>
	/** Entries a fetched document contributes, queued in front of what has not started. */
	expand?: (entry: E, response: Response) => Promise<E[]>
	/** Called as each entry settles, so a progress bar has something to move on. */
	onProgress?: (progress: QueueProgress) => void
	concurrency?: number
}

export interface QueueResult {
	state: DownloadState
	/** The cache keys that hold content, which is what eviction and a retry are counted from. */
	urls: string[]
	bytes: number
	/** Entries no candidate URL answered for. One dead media file must not cost a visitor the tour. */
	failed: string[]
}

export interface QueueProgress {
	done: number
	total: number
}

const DEFAULT_CONCURRENCY = 6

/** Foreground playback wins: the downloader must never starve the track the visitor is listening to. */
const FOREGROUND_CONCURRENCY = 2

const isQuotaExceeded = (error: unknown): boolean =>
	error instanceof DOMException && (error.name === 'QuotaExceededError' || error.code === 22)

const entryKey = ({ urls, cacheKey }: QueueEntry): string => cacheKey ?? urls[0]

/** Entries for a registry row's stored URLs, so a failed download can be re-run from what it recorded. */
export const entriesFromUrls = (urls: string[]): QueueEntry[] => urls.map(url => ({ urls: [url] }))

export class DownloadQueue<E extends QueueEntry = QueueEntry> {
	private pending: E[]
	private readonly deps: QueueDeps<E>
	private readonly concurrency: number
	/** Keys already handed to `process`, so nothing the walk names a second time is fetched twice. */
	private readonly taken = new Set<string>()

	private active = 0
	private stored: string[] = []
	private failed: string[] = []
	private bytes = 0
	private completed = 0
	private total: number
	private outOfSpace = false
	private cancelled = false
	private foregroundBusy = false
	private pump?: () => void

	constructor(entries: E[], deps: QueueDeps<E>) {
		this.pending = [...entries]
		this.total = entries.length
		this.deps = deps
		this.concurrency = deps.concurrency ?? DEFAULT_CONCURRENCY
	}

	get progress(): QueueProgress {
		return { done: this.completed, total: this.total }
	}

	private get limit(): number {
		return this.foregroundBusy ? Math.min(FOREGROUND_CONCURRENCY, this.concurrency) : this.concurrency
	}

	/** Drop the pool while audio or video is loading, and restore it once playback is buffered. */
	setForegroundBusy(busy: boolean): void {
		this.foregroundBusy = busy
		this.pump?.()
	}

	/** Move these URLs to the front, keeping their order — what opening a stop does to the stops around it. */
	prioritize(urls: string[]): void {
		const wanted = new Set(urls)
		const [first, rest] = this.pending.reduce<[E[], E[]]>(
			([front, back], entry) => (wanted.has(entry.urls[0]) ? [[...front, entry], back] : [front, [...back, entry]]),
			[[], []],
		)

		this.pending = [...first, ...rest]
	}

	/** Stop once the fetches already in flight settle. What has landed stays cached. */
	cancel(): void {
		this.cancelled = true
		this.pump?.()
	}

	async run(): Promise<QueueResult> {
		await this.drain()

		if (this.cancelled || this.outOfSpace) return this.result('incomplete')

		// A tour is not downloaded until the worker that serves this deploy's shell is active: without it a
		// cold launch offline opens nothing, and a check mark over a tour whose app cannot open is a lie.
		try {
			await this.deps.workerReady()
		} catch {
			return this.result('error')
		}

		// A failure does not abort the run — one dead media file must not cost a visitor the tour — but it
		// does mean the tour is not whole, and saying so is what offers the visitor a retry.
		return this.result(this.failed.length > 0 ? 'incomplete' : 'complete')
	}

	private result(state: DownloadState): QueueResult {
		return { state, urls: this.stored, bytes: this.bytes, failed: this.failed }
	}

	private drain(): Promise<void> {
		return new Promise(resolve => {
			this.pump = () => {
				if (this.cancelled || this.outOfSpace) {
					if (this.active === 0) resolve()
					return
				}

				while (this.active < this.limit && this.pending.length > 0) {
					const entry = this.pending.shift() as E
					this.taken.add(entryKey(entry))
					this.active += 1
					void this.process(entry).finally(() => {
						this.active -= 1
						this.completed += 1
						this.deps.onProgress?.(this.progress)
						this.pump?.()
					})
				}

				if (this.active === 0 && this.pending.length === 0) resolve()
			}

			this.pump()
		})
	}

	private async process(entry: E): Promise<void> {
		const key = entryKey(entry)

		// Anything the visitor already browsed is cached by the worker, so walking the tour shortens the
		// download rather than duplicating it.
		if (await this.cached(key)) {
			this.stored.push(key)
			return
		}

		for (const url of entry.urls) {
			const response = await this.attempt(url)
			if (!response?.ok) continue

			if (!(await this.store(entry.cacheKey ?? url, response))) return

			this.stored.push(entry.cacheKey ?? url)
			this.bytes += Number(response.headers.get('content-length')) || 0
			await this.enqueueExpansion(entry, response)
			return
		}

		this.failed.push(entry.urls[0])
	}

	private async cached(key: string): Promise<boolean> {
		return Boolean(await this.deps.cache.match(key).catch(() => undefined))
	}

	/** One retry, then give up on this candidate: a flaky connection deserves a second go, a 404 does not. */
	private async attempt(url: string): Promise<Response | undefined> {
		try {
			return await this.deps.fetch(url)
		} catch {
			try {
				return await this.deps.fetch(url)
			} catch {
				return undefined
			}
		}
	}

	/** False once storage is full, which stops the queue rather than failing every remaining entry in turn. */
	private async store(key: string, response: Response): Promise<boolean> {
		try {
			await this.deps.cache.put(key, response.clone())
			return true
		} catch (error) {
			if (isQuotaExceeded(error)) {
				this.outOfSpace = true
				this.pump?.()
				return false
			}

			this.failed.push(key)
			return false
		}
	}

	private async enqueueExpansion(entry: E, response: Response): Promise<void> {
		if (!this.deps.expand) return

		try {
			// The seed carries the manifest's remainder at the back, so most of what a document names is already
			// waiting. Move it rather than queue it twice: the walk's order — a stop, then the media that stop
			// names — is the order a visitor reading behind the downloader needs, and it must win.
			const expanded = (await this.deps.expand(entry, response.clone())).filter(
				candidate => !this.taken.has(entryKey(candidate)),
			)
			const keys = new Set(expanded.map(entryKey))
			const waiting = this.pending.filter(candidate => keys.has(entryKey(candidate)))

			this.pending = [...expanded, ...this.pending.filter(candidate => !keys.has(entryKey(candidate)))]
			this.total += expanded.length - waiting.length
		} catch {
			// A document the expander cannot read still downloaded; only what it would have named is lost.
		}
	}
}
